import { z } from 'zod';
import { SPELL_BAR_SIZE, STATUSES } from '../data/spells.js';
/**
 * Cast intent: "I want to use this spell". Nothing else — no target, no
 * position. A targeted spell hits the mob the player is already fighting (the
 * server's own `attack` target), so a client cannot aim at what it is not
 * engaged with.
 */
export const CastMessageSchema = z.object({
    spellId: z.string().min(1).max(64),
});
/** The owner's energy and cooldowns, sent to them alone after every change. */
export const ResourceEventSchema = z.object({
    energy: z.number().int().min(0),
    maxEnergy: z.number().int().positive(),
    /** Milliseconds until each spell is ready again; absent or 0 means ready. */
    cooldowns: z.record(z.string(), z.number().int().min(0)),
});
export const CastRejectedSchema = z.object({
    spellId: z.string(),
    reason: z.enum([
        'unknown-spell',
        'wrong-class',
        'not-learned',
        'not-on-bar',
        'cooldown',
        'energy',
        'no-target',
        'out-of-range',
        'dead',
        'mounted',
        /** A charge found no free tile next to its target. */
        'no-room',
    ]),
});
/** A spell went off, for everyone nearby: the caster's pose and a heal's number. */
export const CastEventSchema = z.object({
    /** Session id of the caster. */
    id: z.string().min(1),
    spellId: z.string().min(1),
    /** Mob instance struck by a targeted spell, so the impact is drawn on it. */
    targetId: z.string().min(1).optional(),
    /** Health restored, for a heal. */
    healed: z.number().int().min(0).optional(),
    /** Where a leap set the caster down, so the bound is drawn from where they stood. */
    leapTo: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
    /** Tiles a line spell swept, for the wave to run along them. */
    tiles: z.array(z.object({ x: z.number().int(), y: z.number().int() })).max(16).optional(),
});
/**
 * Grimoire (ADR 0048): the spells the owner knows, derived from their level
 * and the quests they turned in, and the four they fight with. Sent to the
 * owner alone on joining and whenever either changes.
 */
export const SpellsEventSchema = z.object({
    known: z.array(z.string().min(1)),
    bar: z.array(z.string().min(1).nullable()).length(SPELL_BAR_SIZE),
    /** Spells learnt by what just happened (a level, a quest), for the banner; absent on joining. */
    learnt: z.array(z.string().min(1)).optional(),
});
/** Intent: fight with these four spells. Out of combat only; the server checks each is known. */
export const SetSpellBarMessageSchema = z.object({
    bar: z.array(z.string().min(1).max(64).nullable()).length(SPELL_BAR_SIZE),
});
export const SpellBarRejectedSchema = z.object({
    reason: z.enum(['not-learned', 'duplicate', 'wrong-size', 'in-combat', 'dead']),
});
/**
 * A timed state landed on a creature or a player (ADR 0048), for everyone
 * nearby to draw it for `durationMs`. It needs no end message: it wears off
 * on the client's own clock, and a death takes the view away with it.
 */
export const StatusEventSchema = z.object({
    target: z.enum(['mob', 'player']),
    /** Mob instance id, or the player's session id. */
    id: z.string().min(1),
    status: z.enum(STATUSES),
    durationMs: z.number().int().min(0),
});
//# sourceMappingURL=cast.js.map